import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { auth } from '../firebase';
import CatalogCapacityHeadline from '../catalog/CatalogCapacityHeadline';
import { fetchContentManifest } from '../catalog/catalogApi';
import { catalogCapacity } from '../catalog/catalogCapacity';
import {
  CONTROL_SLIDE_IMAGE_COUNT,
  buildControlSlides,
  controlIdentifierForImage,
} from './controlSlides';
import { updateControlSlideState } from './controlSlideApi';
import ControlSlidePreview from './ControlSlidePreview';

function documents(snapshot) {
  return snapshot.docs.map((document) => ({ id: document.id, ...document.data() }));
}

function stateCopy(slide) {
  if (!slide.ready) {
    return slide.enabled
      ? 'Incomplete set · included in manifest'
      : 'Incomplete set · excluded from manifest';
  }
  return slide.enabled ? 'Included in manifest' : 'Excluded from manifest';
}

export default function ViewControlSlides() {
  const navigate = useNavigate();
  const location = useLocation();
  const [organizations, setOrganizations] = useState([]);
  const [images, setImages] = useState([]);
  const [records, setRecords] = useState([]);
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [capacity, setCapacity] = useState(null);
  const [capacityLoading, setCapacityLoading] = useState(true);
  const [capacityError, setCapacityError] = useState('');
  const [togglingKey, setTogglingKey] = useState('');
  const [toggleErrors, setToggleErrors] = useState({});
  const [previewSlide, setPreviewSlide] = useState(null);

  const loadSlides = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const db = getFirestore();
      const [organizationSnapshot, imageSnapshot, slideSnapshot] = await Promise.all([
        getDocs(collection(db, 'organizations')),
        getDocs(collection(db, 'images')),
        getDocs(collection(db, 'control_slides')),
      ]);
      const rawImages = documents(imageSnapshot).filter(
        (image) => controlIdentifierForImage(image) != null,
      );
      const storage = getStorage();
      const hydratedImages = await Promise.all(rawImages.map(async (image) => {
        const storagePath = image.key || (image.hash ? `vasi/stimuli/${image.hash}.jpg` : '');
        if (!storagePath) {
          return { ...image, imageUrl: '' };
        }
        try {
          return { ...image, imageUrl: await getDownloadURL(ref(storage, storagePath)) };
        } catch (error) {
          console.warn(`Could not load control image ${image.hash || image.id}:`, error);
          return { ...image, imageUrl: '' };
        }
      }));

      setOrganizations(documents(organizationSnapshot));
      setImages(hydratedImages);
      setRecords(documents(slideSnapshot));
    } catch (error) {
      console.error('Failed to load control slides:', error);
      setPageError('Control slides could not be loaded. Refresh the page or try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCapacity = useCallback(async () => {
    setCapacityLoading(true);
    setCapacityError('');
    try {
      const token = await auth.currentUser.getIdToken();
      const manifest = await fetchContentManifest({ token });
      setCapacity(catalogCapacity(manifest.images));
    } catch (error) {
      console.error('Failed to load global control capacity:', error);
      setCapacityError(error.message || 'Global catalog capacity could not be loaded.');
    } finally {
      setCapacityLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlides();
    loadCapacity();
  }, [loadSlides, loadCapacity]);

  const slides = useMemo(() => buildControlSlides({ organizations, images, records }), [
    organizations,
    images,
    records,
  ]);
  const filteredSlides = organizationFilter === 'all'
    ? slides
    : slides.filter((slide) => slide.organizationId === organizationFilter);

  const toggleSlide = async (slide) => {
    if (togglingKey) {
      return;
    }
    const enabled = !slide.enabled;
    setTogglingKey(slide.key);
    setToggleErrors((current) => ({ ...current, [slide.key]: '' }));
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await updateControlSlideState({
        token,
        organizationId: slide.organizationId,
        controlIdentifier: slide.identifier,
        enabled,
      });
      const returnedSlide = response.slide || {};
      const nextRecord = {
        id: `${slide.organizationId}--${slide.identifier}`,
        organization_id: slide.organizationId,
        control_identifier: slide.identifier,
        description: returnedSlide.description ?? slide.description,
        enabled,
      };
      setRecords((current) => {
        const existingIndex = current.findIndex((record) => (
          record.organization_id === slide.organizationId
          && Number(record.control_identifier) === slide.identifier
        ));
        if (existingIndex === -1) {
          return [...current, nextRecord];
        }
        return current.map((record, index) => (
          index === existingIndex ? { ...record, ...nextRecord } : record
        ));
      });
      await loadCapacity();
    } catch (error) {
      console.error('Failed to update control slide:', error);
      setToggleErrors((current) => ({
        ...current,
        [slide.key]: error.message || 'Control slide could not be updated.',
      }));
    } finally {
      setTogglingKey('');
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <header className="sm:flex sm:items-end sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Control Slides</h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-700">
            Manage grouped controls as release units. Every image in an enabled group is sent to devices.
          </p>
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:ml-8 sm:mt-0 sm:flex-row sm:items-end">
          <div>
            <label htmlFor="control-slide-organization-filter" className="block text-sm font-medium text-gray-900">
              Organization
            </label>
            <select
              id="control-slide-organization-filter"
              value={organizationFilter}
              onChange={(event) => setOrganizationFilter(event.target.value)}
              className="mt-1 block min-w-[15rem] rounded-md border-gray-300 bg-white py-2 pl-3 pr-10 text-sm shadow-sm focus:border-indigo-600 focus:ring-indigo-600"
            >
              <option value="all">All organizations</option>
              {organizations.map((organization) => (
                <option key={organization.id} value={organization.id}>
                  {organization.name || organization.id}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => navigate('/control-slides/new')}
            className="inline-flex items-center justify-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
            New control slide
          </button>
        </div>
      </header>

      <CatalogCapacityHeadline
        kind="control"
        capacity={capacity}
        loading={capacityLoading}
        error={capacityError}
      />

      {location.state?.successMessage && (
        <div role="status" className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
          {location.state.successMessage}
        </div>
      )}

      {loading && (
        <div className="mt-12 flex items-center justify-center gap-3 text-sm text-gray-600">
          <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading control slides…
        </div>
      )}

      {!loading && pageError && (
        <div role="alert" className="mt-8 rounded-lg border border-red-200 bg-red-50 p-5">
          <div className="flex gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-red-600" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-red-900">{pageError}</p>
              <button
                type="button"
                onClick={loadSlides}
                className="mt-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-800 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !pageError && filteredSlides.length === 0 && (
        <section className="mt-10 border-y border-gray-200 py-16 text-center">
          <PhotoIcon className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />
          <h2 className="mt-3 text-base font-semibold text-gray-900">No control slides yet</h2>
          <p className="mt-1 text-sm text-gray-600">
            Create a slide by uploading ten control images with one shared identifier.
          </p>
        </section>
      )}

      {!loading && !pageError && filteredSlides.length > 0 && (
        <section aria-label="Control slide library" className="mt-8 divide-y divide-gray-200 border-y border-gray-200">
          {filteredSlides.map((slide) => {
            const slotCount = Math.max(CONTROL_SLIDE_IMAGE_COUNT, slide.imageCount);
            const toggling = togglingKey === slide.key;
            return (
              <article key={slide.key} className="py-7 first:pt-6">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
                  <div className="w-full xl:w-64 xl:shrink-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-900">Control {slide.identifier}</h2>
                      {!slide.explicit && (
                        <span className="rounded-md bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-700/10">
                          Imported from stimulus metadata
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm font-medium text-gray-700">{slide.organizationName}</p>
                    <p className="mt-3 text-sm leading-6 text-gray-600">
                      {slide.description || 'No description'}
                    </p>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
                      {Array.from({ length: slotCount }, (_unused, index) => {
                        const image = slide.images[index];
                        return (
                          <div
                            key={image?.hash || image?.id || `empty-${index}`}
                            className="relative aspect-[2/3] overflow-hidden rounded-md bg-gray-100 ring-1 ring-inset ring-gray-200"
                          >
                            {image?.imageUrl ? (
                              <img
                                data-testid={`slide-${slide.identifier}-image`}
                                src={image.imageUrl}
                                alt={`Control ${slide.identifier}, image ${index + 1}`}
                                className="h-full w-full object-cover"
                              />
                            ) : image ? (
                              <div
                                data-testid={`slide-${slide.identifier}-image`}
                                className="flex h-full items-center justify-center px-1 text-center text-[10px] font-medium text-gray-500"
                              >
                                Preview unavailable
                              </div>
                            ) : (
                              <div className="flex h-full items-center justify-center text-xs font-semibold text-gray-400">
                                {index + 1}
                              </div>
                            )}
                            {image && (
                              <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                {image.control || index + 1}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-5 xl:w-56 xl:shrink-0 xl:flex-col xl:items-end">
                    <div className="text-left xl:text-right">
                      <p className={`text-sm font-semibold ${slide.ready ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {slide.imageCount} / {CONTROL_SLIDE_IMAGE_COUNT} images
                      </p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-600 xl:justify-end">
                        {slide.ready ? (
                          <CheckCircleIcon className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                        ) : (
                          <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" aria-hidden="true" />
                        )}
                        {stateCopy(slide)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPreviewSlide(slide)}
                      disabled={slide.imageCount === 0}
                      aria-label={`Preview Control ${slide.identifier}`}
                      className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Preview slide
                    </button>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={slide.enabled}
                      aria-label={`${slide.enabled ? 'Disable' : 'Enable'} Control ${slide.identifier}`}
                      disabled={Boolean(togglingKey)}
                      onClick={() => toggleSlide(slide)}
                      className={`relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-transparent transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 ${
                        slide.enabled ? 'bg-emerald-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className="sr-only">{toggling ? 'Updating' : stateCopy(slide)}</span>
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow ring-0 transition-transform duration-150 ${
                          slide.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    {toggleErrors[slide.key] && (
                      <p role="alert" className="max-w-xs text-sm font-medium text-red-700 xl:text-right">
                        {toggleErrors[slide.key]}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      )}
      {previewSlide && (
        <ControlSlidePreview
          key={previewSlide.key}
          slide={previewSlide}
          onClose={() => setPreviewSlide(null)}
        />
      )}
    </div>
  );
}
